import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { API_BASE_URL } from '@/lib/constants';
import { weddingKeys } from '@/hooks/useWedding';
import type { Wedding, WeddingEvent } from '@/lib/types';

export function useCreateShare(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { sharePin?: string }) =>
      api.post<{ shareId: string; sharingEnabled: boolean }>(`/api/weddings/${weddingId}/share`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: weddingKeys.detail(weddingId) });
    },
  });
}

export function useRegenerateShare(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { sharePin?: string }) =>
      api.post<{ shareId: string; sharingEnabled: boolean }>(`/api/weddings/${weddingId}/share/regenerate`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: weddingKeys.detail(weddingId) });
    },
  });
}

export function useDisableShare(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ disabled: boolean }>(`/api/weddings/${weddingId}/share/disable`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: weddingKeys.detail(weddingId) });
    },
  });
}

export function useSharedTimeline(shareId: string, sharePin: string | undefined) {
  return useQuery({
    queryKey: ['sharedTimeline', shareId, sharePin],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (sharePin) headers['X-Share-PIN'] = sharePin;
      const res = await fetch(`${API_BASE_URL}/api/share/${shareId}`, {
        headers,
        credentials: 'include',
      });
      if (res.status === 401) {
        const body = (await res.json()) as { pinRequired?: boolean };
        const err: Error & { pinRequired?: boolean } = new Error('PIN required');
        err.pinRequired = body.pinRequired;
        throw err;
      }
      if (!res.ok) throw new Error('Not found');
      const body = (await res.json()) as {
        success: true;
        data: { wedding: Wedding; events: WeddingEvent[] };
      };
      return body.data;
    },
    refetchInterval: 10_000,
    retry: false,
    enabled: true,
  });
}
